import React, { useEffect, useState, useMemo } from "react";
import apiClient from "../api/apiClient";
import { Link, useLocation } from "react-router-dom";
import InfiniteScroll from "../components/InfiniteScroll";
import LoadingSpinner from "../components/LoadingSpinner";
import FiltersPanel from "../components/FiltersPanel";
import BundleListingDetails from "../Dashboard-User/BundleDetails";
import MockupCard from "../components/MockupCard";
import NotFound from "../components/NotFound";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { toggledFilterArray } from "../utils/filterSelection";

export const Searchpage = () => {
  const [showBundleListings, setShowBundleListings] = useState(false);
  const [bundleListingsById, setBundleListingsById] = useState(false);
  const [printingProviders, setPrintingProviders] = useState([]);
  const [bundleListingsId, setBundleListingsId] = useState(false);

  const location = useLocation();
  const data = location?.state;

  const [filtersChanged, setFiltersChanged] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const defaultTypes = ["AI", "Real", "3D", "Mix"];
  const defaultPrice = ["Free", "Paid"];
  const defaultSizes = ["Small", "Medium", "Large"];
  const defaultAmount = ["Single", "Bundle"];

  /** Checkbox state — updates without refetching until Apply. */
  const [draftAmount, setDraftAmount] = useState(defaultAmount);
  const [draftTypes, setDraftTypes] = useState(defaultTypes);
  const [draftProviders, setDraftProviders] = useState([]);
  const [draftPrice, setDraftPrice] = useState(defaultPrice);
  const [draftSizes, setDraftSizes] = useState(defaultSizes);

  /** Values passed to API / infinite scroll after Apply. */
  const [appliedAmount, setAppliedAmount] = useState(defaultAmount);
  const [appliedTypes, setAppliedTypes] = useState(defaultTypes);
  const [appliedProviders, setAppliedProviders] = useState([]);
  const [appliedPrice, setAppliedPrice] = useState(defaultPrice);
  const [appliedSizes, setAppliedSizes] = useState(defaultSizes);

  const getCurrentURLParameters = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const params = {};
    for (const [key, value] of searchParams) {
      params[key] = value;
    }
    return params;
  };

  const { price, order_by_sales, title, overAllSearch, bundle, subcategory , featured_section } =
    getCurrentURLParameters();

  const hasSearchContext = Boolean(
    overAllSearch?.trim() ||
      title?.trim() ||
      subcategory ||
      bundle ||
      featured_section ||
      price ||
      order_by_sales ||
      filtersChanged,
  );

  let basturl;
  let bundleBaseUrl;

  const buildFilterQuery = () => {
    const query = new URLSearchParams();

    if (appliedTypes.length > 0) {
      appliedTypes.forEach((type) => query.append("type", type));
    }

    if (appliedProviders.length > 0) {
      appliedProviders.forEach((provider) =>
        query.append("provider", provider),
      );
    }

    if (appliedPrice.length === 1) {
      query.append("price", appliedPrice.includes("Free") ? "free" : "price");
    }

    if (appliedSizes.length > 0) {
      appliedSizes.forEach((size) => query.append("size", size.toLowerCase()));
    }

    return query.toString();
  };

  // Combined fetch function for infinite scroll
  const fetchCombinedData = useMemo(() => {
    return async (page) => {
      const baseParams = new URLSearchParams();
      let basturl;
      let bundleBaseUrl;

      if (bundle === "bundle") {
        // Only fetch bundles
        if (overAllSearch) {
          baseParams.append("search", overAllSearch);
          bundleBaseUrl = "bundle-listings";
        }
        if (price === "free") baseParams.append("price", "free");
        if (subcategory) baseParams.append("subcategory", subcategory);
        baseParams.append("page_size", "16");

        let finalURL = `/${bundleBaseUrl || "bundle-listings"}/?page=${page}&calledFrom=home&${baseParams.toString()}`;
        if (filtersChanged) {
          const filterQuery = buildFilterQuery();
          finalURL += `&${filterQuery}`;
        }

        const result = await apiClient.get(finalURL);
        return {
          ok: result?.ok,
          data: {
            results: result?.data?.results || [],
            count: result?.data?.count || 0,
          },
        };
      }

      // Fetch both single and bundle listings
      const amounts = appliedAmount.map((t) => t.toLowerCase());
      const shouldFetchSingle = featured_section ? true : amounts.includes("single");
      const shouldFetchBundle = featured_section ? false : amounts.includes("bundle");

      if (price === "free") baseParams.append("price", "free");
      if (subcategory) baseParams.append("subcategory", subcategory);
      if (featured_section) baseParams.append("featured_section", featured_section);
      if (price === "price") basturl = "featured-listings/ALL";
      if (order_by_sales) basturl = "most-sold-listings";
      if (overAllSearch) {
        baseParams.append("search", overAllSearch);
        basturl = "listings";
      }

      baseParams.append("page_size", "16");

      const promises = [];

      if (shouldFetchSingle) {
        let finalURL = `/${basturl || "listings"}/?page=${page}&${baseParams.toString()}`;
        if (filtersChanged) {
          const filterQuery = buildFilterQuery();
          finalURL += `&${filterQuery}`;
        }
        promises.push(apiClient.get(finalURL));
      }

      if (shouldFetchBundle && !price) {
        const bundleParams = new URLSearchParams();
        if (overAllSearch) {
          bundleParams.append("search", overAllSearch);
          bundleBaseUrl = "bundle-listings";
        }
        if (price === "free") bundleParams.append("price", "free");
        if (subcategory) bundleParams.append("subcategory", subcategory);
        bundleParams.append("page_size", "16");

        let finalURL = `/${bundleBaseUrl || "bundle-listings"}/?page=${page}&calledFrom=home&${bundleParams.toString()}`;
        if (filtersChanged) {
          const filterQuery = buildFilterQuery();
          finalURL += `&${filterQuery}`;
        }
        promises.push(apiClient.get(finalURL));
      }

      if (promises.length === 0) {
        return { ok: false, data: { results: [], count: 0 } };
      }

      const results = await Promise.all(promises);
      const mockupsResult = shouldFetchSingle ? results[0] : null;
      const bundlesResult =
        shouldFetchBundle && !price
          ? shouldFetchSingle
            ? results[1]
            : results[0]
          : null;

      const mockups = mockupsResult?.ok ? mockupsResult.data.results || [] : [];
      const bundles = bundlesResult?.data?.results || [];

      // Combine and sort by created_at
      const combined = [...bundles, ...mockups].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      );

      const count =
        mockupsResult?.data?.count || bundlesResult?.data?.count || 0;

      return {
        ok: mockupsResult?.ok || bundlesResult?.ok,
        data: {
          results: combined,
          count: count,
        },
      };
    };
  }, [
    price,
    order_by_sales,
    title,
    overAllSearch,
    bundle,
    subcategory,
    featured_section,
    appliedAmount,
    appliedTypes,
    appliedProviders,
    appliedPrice,
    appliedSizes,
    filtersChanged,
  ]);

  // Use infinite scroll hook
  const {
    data: records,
    loading,
    hasMore,
    loadMore,
  } = useInfiniteScroll(
    fetchCombinedData,
    [
      location.search,
      appliedTypes,
      appliedProviders,
      appliedPrice,
      appliedSizes,
      appliedAmount,
      filtersChanged,
    ],
    16,
  );

  const fetchBundleDetail = async () => {
    try {
      const result = await apiClient.get(
        `/bundle-listings/${bundleListingsId}/`,
      );
      if (!result.ok) return;
      setBundleListingsById(result?.data || {});
      setShowBundleListings(true);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (bundleListingsId) {
      fetchBundleDetail();
    }
  }, [bundleListingsId]);

  const fetchPrintProviders = async () => {
    const result = await apiClient.get(
      `/print-providers/?products_not_required=1`,
    );
    if (result.ok) {
      const list = result?.data?.results || [];
      const ids = list.map((item) => item?.id);
      setPrintingProviders(list);
      setAppliedProviders(ids);
      setDraftProviders(ids);
    }
  };

  useEffect(() => {
    fetchPrintProviders();
  }, []);

  useEffect(() => {
    setShowBundleListings(false);
    setBundleListingsId(null);
  }, [records]);

  const toggleDraftSelection = (value, array, type) => {
    const newArray = toggledFilterArray(value, array);
    if (newArray === null) return;

    switch (type) {
      case "type":
        setDraftTypes(newArray);
        break;
      case "provider":
        setDraftProviders(newArray);
        break;
      case "price":
        setDraftPrice(newArray);
        break;
      case "size":
        setDraftSizes(newArray);
        break;
      case "amount":
        setDraftAmount(newArray);
        break;
      default:
        break;
    }
  };

  const handleApplyFilters = () => {
    setAppliedTypes([...draftTypes]);
    setAppliedProviders([...draftProviders]);
    setAppliedPrice([...draftPrice]);
    setAppliedSizes([...draftSizes]);
    setAppliedAmount([...draftAmount]);
    setFiltersChanged(true);
  };

  const handleClearFilters = () => {
    const allProviderIds = printingProviders.map((p) => p.id);
    setDraftTypes([...defaultTypes]);
    setDraftPrice([...defaultPrice]);
    setDraftAmount([...defaultAmount]);
    setDraftSizes([...defaultSizes]);
    setDraftProviders(allProviderIds);
    setAppliedTypes([...defaultTypes]);
    setAppliedPrice([...defaultPrice]);
    setAppliedAmount([...defaultAmount]);
    setAppliedSizes([...defaultSizes]);
    setAppliedProviders(allProviderIds);
    setFiltersChanged(true);
  };

  return (
    <>
      {!showBundleListings ? (
        <div>
          {loading && records?.length === 0 ? (
            <div className="d-flex justify-content-center mt-5">
              <LoadingSpinner />
            </div>
          ) : (
            <section className="py-5" style={{ backgroundColor: "#E3EBE0" }}>
              <div className="container">
                {records?.length > 0 && (
                  <FiltersPanel
                    expanded={showFilters}
                    onToggle={() => setShowFilters((prev) => !prev)}
                    onClearAll={handleClearFilters}
                    selectedTypes={draftTypes}
                    selectedProviders={draftProviders}
                    selectedPrice={draftPrice}
                    selectedSizes={draftSizes}
                    selectedAmount={draftAmount}
                    toggleSelection={toggleDraftSelection}
                    onApplyFilters={handleApplyFilters}
                    printingProviders={printingProviders}
                  />
                )}

                {/* Heading & Description */}
                <h2 className="text-start fw-bold mb-1">{data?.heading}</h2>
                <p className="text-start mb-4">{data?.des}</p>

                {/* Search Results */}
                {records?.length > 0 ? (
                  <InfiniteScroll
                    onLoadMore={loadMore}
                    hasMore={hasMore}
                    loading={loading}
                  >
                    <div className="row">
                      {records.map((item, index) => (
                        <MockupCard
                          key={item?.id || index}
                          imageUrl={
                            item?.use_main_file_as_thumbnail
                              ? item?.compressed_file
                              : item?.thumbnails?.[0]?.image
                          }
                          title={item?.listing?.title || item?.title}
                          description={
                            item?.listing?.short_description ||
                            item?.short_description
                          }
                          price={item?.listing?.price || item?.price}
                          seller={item?.user_name}
                          id={item?.listing?.id || item?.id}
                          item={item?.listing || item}
                          col={3}
                          setBundleListingsById={setBundleListingsById}
                          setShowBundleListings={setShowBundleListings}
                          setBundleListingsId={setBundleListingsId}
                          thumbnails={item?.thumbnails || []}
                        />
                      ))}
                    </div>
                  </InfiniteScroll>
                ) : (
                  <NotFound hasActiveSearch={hasSearchContext} />
                )}
              </div>
            </section>
          )}
        </div>
      ) : (
        <BundleListingDetails
          bundleListingsById={bundleListingsById}
          loader={loading}
          setShowBundleListings={setShowBundleListings}
          setBundleListingsId={setBundleListingsId}
        />
      )}
    </>
  );
};
